import type {
  BudgetSheetCellValue,
  BudgetSheetColumn,
  BudgetSheetGridRow,
  BudgetSheetState,
  BudgetSheetSuggestedSnapshot
} from '../../services/projectAutofillDraftService.ts'

export const BUDGET_TEMPLATE_VERSION = 'edify-budget-v1'

export const BUDGET_BUILTIN_COLUMN_IDS = [
  'item',
  'unit',
  'quantity',
  'unitPrice',
  'total',
  'notes'
] as const

export type BudgetBuiltinColumnId = (typeof BUDGET_BUILTIN_COLUMN_IDS)[number]

export const MAX_BUDGET_SHEET_ROWS = 200
export const MAX_BUDGET_CELL_CHARS = 2000
export const MAX_BUDGET_DISPLAY_LABEL = 80
export const MAX_CUSTOM_COLUMNS = 2
export const CUSTOM_COLUMN_ID_PATTERN = /^custom_[a-zA-Z0-9-]+$/

export type BudgetSheetRubroRef = {
  tempId: string
  detectedLabel: string
  selectedSkillId?: number
}

export function rubroHasSkillForBudgetBinding(r: BudgetSheetRubroRef): boolean {
  const id = r.selectedSkillId
  return typeof id === 'number' && Number.isFinite(id) && id > 0
}

export function defaultBudgetColumnsV1(): BudgetSheetColumn[] {
  return BUDGET_BUILTIN_COLUMN_IDS.map((id) => ({ id }))
}

function newRowId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

function cloneRows(rows: BudgetSheetGridRow[]): BudgetSheetGridRow[] {
  return rows.map((row) => ({
    rowId: row.rowId,
    cells: { ...row.cells }
  }))
}

function cloneColumns(cols: BudgetSheetColumn[]): BudgetSheetColumn[] {
  return cols.map((c) => ({ ...c }))
}

export function buildBudgetBinding(rubro: BudgetSheetRubroRef): BudgetSheetState['binding'] {
  const rubroLabel = String(rubro.detectedLabel || '').trim() || rubro.tempId
  const base: BudgetSheetState['binding'] = {
    tempId: rubro.tempId,
    rubroLabel
  }
  if (rubroHasSkillForBudgetBinding(rubro)) {
    base.selectedSkillId = rubro.selectedSkillId
  }
  return base
}

export function buildDefaultBudgetSheetState(rubro: BudgetSheetRubroRef): BudgetSheetState {
  return {
    templateVersion: BUDGET_TEMPLATE_VERSION,
    binding: buildBudgetBinding(rubro),
    columns: defaultBudgetColumnsV1(),
    rows: [],
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function attachBudgetBindingToState(
  rubro: BudgetSheetRubroRef,
  state: BudgetSheetState
): BudgetSheetState {
  return {
    ...state,
    binding: buildBudgetBinding(rubro)
  }
}

export function normalizeCellString(raw: string): string {
  const s = raw.length > MAX_BUDGET_CELL_CHARS ? raw.slice(0, MAX_BUDGET_CELL_CHARS) : raw
  return s
}

function isNumericColumnId(id: string): boolean {
  return id === 'quantity' || id === 'unitPrice' || id === 'total'
}

function parseCellForColumn(colId: string, raw: string): BudgetSheetCellValue {
  const trimmed = raw.trim()
  if (trimmed === '') return ''
  if (isNumericColumnId(colId)) {
    const n = Number(trimmed.replace(',', '.'))
    if (Number.isFinite(n)) return n
    return normalizeCellString(raw)
  }
  return normalizeCellString(raw)
}

export function applyBudgetSheetCellChange(
  state: BudgetSheetState,
  rowId: string,
  colId: string,
  raw: string
): BudgetSheetState {
  const colIds = new Set(state.columns.map((c) => c.id))
  if (!colIds.has(colId)) return state
  const nextRows = state.rows.map((row) => {
    if (row.rowId !== rowId) return row
    const nextCells = { ...row.cells }
    nextCells[colId] = parseCellForColumn(colId, raw)
    return { ...row, cells: nextCells }
  })
  return {
    ...state,
    rows: nextRows,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function addBudgetSheetRow(state: BudgetSheetState): BudgetSheetState | null {
  if (state.rows.length >= MAX_BUDGET_SHEET_ROWS) return null
  const empty: Record<string, BudgetSheetCellValue> = {}
  for (const c of state.columns) {
    empty[c.id] = ''
  }
  const nextRows = [...state.rows, { rowId: newRowId(), cells: empty }]
  return {
    ...state,
    rows: nextRows,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function removeBudgetSheetRow(state: BudgetSheetState, rowId: string): BudgetSheetState {
  const nextRows = state.rows.filter((r) => r.rowId !== rowId)
  return {
    ...state,
    rows: nextRows,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function resetBudgetFromSnapshot(state: BudgetSheetState): BudgetSheetState | null {
  const snap = state.suggestedSnapshot
  if (!snap || !Array.isArray(snap.columns) || !Array.isArray(snap.rows)) return null
  return {
    ...state,
    columns: cloneColumns(snap.columns),
    rows: cloneRows(snap.rows),
    source: 'reset',
    updatedAt: new Date().toISOString()
  }
}

export function isBudgetSheetTemplateSupported(state: BudgetSheetState): boolean {
  return state.templateVersion === BUDGET_TEMPLATE_VERSION
}

export function isBuiltinBudgetColumnId(id: string): boolean {
  return (BUDGET_BUILTIN_COLUMN_IDS as readonly string[]).includes(id)
}

export function isCustomBudgetColumnId(id: string): boolean {
  return (
    CUSTOM_COLUMN_ID_PATTERN.test(id) &&
    id.length <= 40 &&
    !isBuiltinBudgetColumnId(id)
  )
}

export function countCustomBudgetColumns(columns: BudgetSheetColumn[]): number {
  return columns.filter((c) => isCustomBudgetColumnId(c.id)).length
}

function newCustomColumnId(existing: BudgetSheetColumn[]): string {
  for (let i = 0; i < 20; i++) {
    const suffix =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
    let id = `custom_${suffix}`
    if (id.length > 40) id = id.slice(0, 40)
    if (!existing.some((c) => c.id === id)) return id
  }
  return `custom_${Date.now().toString(36)}`.slice(0, 40)
}

export function addCustomBudgetColumn(
  state: BudgetSheetState,
  displayLabelRaw: string
): BudgetSheetState | null {
  if (countCustomBudgetColumns(state.columns) >= MAX_CUSTOM_COLUMNS) return null
  const label = String(displayLabelRaw || '')
    .trim()
    .slice(0, MAX_BUDGET_DISPLAY_LABEL)
  if (!label) return null
  const id = newCustomColumnId(state.columns)
  const newCol: BudgetSheetColumn = { id, displayLabel: label }
  const nextColumns = [...state.columns, newCol]
  const nextRows = state.rows.map((row) => ({
    ...row,
    cells: { ...row.cells, [id]: '' }
  }))
  return {
    ...state,
    columns: nextColumns,
    rows: nextRows,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function removeCustomBudgetColumn(state: BudgetSheetState, colId: string): BudgetSheetState {
  if (!isCustomBudgetColumnId(colId)) return state
  const nextColumns = state.columns.filter((c) => c.id !== colId)
  const nextRows = state.rows.map((row) => {
    const cells = { ...row.cells }
    delete cells[colId]
    return { ...row, cells }
  })
  return {
    ...state,
    columns: nextColumns,
    rows: nextRows,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function updateBudgetColumnDisplayLabel(
  state: BudgetSheetState,
  colId: string,
  rawLabel: string
): BudgetSheetState {
  const trimmed = String(rawLabel ?? '')
    .trim()
    .slice(0, MAX_BUDGET_DISPLAY_LABEL)
  if (isCustomBudgetColumnId(colId) && !trimmed) return state
  const nextColumns = state.columns.map((c) => {
    if (c.id !== colId) return c
    if (isBuiltinBudgetColumnId(c.id)) {
      if (!trimmed) return { id: c.id }
      return { id: c.id, displayLabel: trimmed }
    }
    return { id: c.id, displayLabel: trimmed }
  })
  return {
    ...state,
    columns: nextColumns,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function validateBudgetSheetStateClient(state: BudgetSheetState): string | null {
  if (state.templateVersion !== BUDGET_TEMPLATE_VERSION) {
    return 'Versión de plantilla no soportada'
  }
  const binding = state.binding
  if (!binding.tempId || String(binding.tempId).trim() === '') return 'binding.tempId requerido'
  if (!binding.rubroLabel || String(binding.rubroLabel).trim() === '') return 'binding.rubroLabel requerido'
  if (!state.columns.length) return 'Se requiere al menos una columna'
  let customCount = 0
  const colIds: string[] = []
  for (const col of state.columns) {
    const id = String(col.id || '').trim()
    if (!id) return 'Columna sin id'
    if (colIds.includes(id)) return 'Ids de columna duplicados'
    colIds.push(id)
    if (isBuiltinBudgetColumnId(id)) {
      if (col.displayLabel != null && String(col.displayLabel).length > MAX_BUDGET_DISPLAY_LABEL) {
        return 'displayLabel demasiado largo'
      }
      continue
    }
    if (!CUSTOM_COLUMN_ID_PATTERN.test(id) || id.length > 40) return `id de columna no permitido: ${id}`
    customCount += 1
    if (customCount > MAX_CUSTOM_COLUMNS) return 'máximo 2 columnas custom'
    if (!col.displayLabel || String(col.displayLabel).trim() === '') return 'custom requiere displayLabel'
    if (String(col.displayLabel).length > MAX_BUDGET_DISPLAY_LABEL) return 'displayLabel demasiado largo'
  }
  if (state.rows.length > MAX_BUDGET_SHEET_ROWS) return 'máximo 200 filas'
  const colSet = new Set(colIds)
  for (const row of state.rows) {
    if (!row.rowId || String(row.rowId).trim() === '') return 'Fila sin rowId'
    const cells = row.cells || {}
    for (const k of Object.keys(cells)) {
      if (!colSet.has(k)) return `clave desconocida en cells: ${k}`
      const v = cells[k]
      if (v === null || v === undefined) continue
      if (typeof v === 'number' && !Number.isFinite(v)) return 'Valor numérico inválido'
      const str = typeof v === 'number' ? String(v) : String(v)
      if (str.length > MAX_BUDGET_CELL_CHARS) return 'Texto de celda demasiado largo'
    }
  }
  if (state.suggestedSnapshot) {
    const inner = state.suggestedSnapshot as BudgetSheetSuggestedSnapshot & { suggestedSnapshot?: unknown }
    if (inner.suggestedSnapshot != null) return 'suggestedSnapshot no debe anidarse'
  }
  return null
}

export function serializeBudgetSheetForDirtySnapshot(state: BudgetSheetState | undefined): string {
  if (!state) return ''
  const stable = {
    templateVersion: state.templateVersion,
    source: state.source,
    columns: [...state.columns]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((c) => ({
        id: c.id,
        displayLabel: c.displayLabel != null && String(c.displayLabel).trim() !== '' ? String(c.displayLabel) : ''
      })),
    rows: [...state.rows]
      .sort((a, b) => a.rowId.localeCompare(b.rowId))
      .map((r) => ({
        rowId: r.rowId,
        cells: Object.keys(r.cells || {})
          .sort()
          .reduce(
            (acc, k) => {
              acc[k] = r.cells[k]
              return acc
            },
            {} as Record<string, BudgetSheetCellValue>
          )
      })),
    snap: state.suggestedSnapshot
      ? {
          columns: state.suggestedSnapshot.columns?.map((c) => c.id).join(','),
          rows: state.suggestedSnapshot.rows?.length
        }
      : null
  }
  return JSON.stringify(stable)
}

const BUDGET_BUILTIN_LABEL_ES: Record<string, string> = {
  item: 'Ítem o descripción',
  unit: 'Unidad',
  quantity: 'Cantidad',
  unitPrice: 'Precio unitario',
  total: 'Total',
  notes: 'Observaciones'
}

export function defaultBudgetColumnCaptionEs(id: string): string {
  const v = BUDGET_BUILTIN_LABEL_ES[id]
  if (v) return v
  if (isCustomBudgetColumnId(id)) return 'Columna personalizada'
  return id
}

export function columnHeaderLabel(col: BudgetSheetColumn): string {
  if (col.displayLabel && String(col.displayLabel).trim()) return String(col.displayLabel).trim()
  return defaultBudgetColumnCaptionEs(col.id)
}

export type BudgetSheetReadonlyReason = 'none' | 'version' | 'budget_slot' | 'no_skill'

export function resolveBudgetSheetReadonlyReason(
  rubro: BudgetSheetRubroRef,
  bs: BudgetSheetState | undefined,
  budgetSlotHasFileOrPages: boolean
): BudgetSheetReadonlyReason {
  if (bs && !isBudgetSheetTemplateSupported(bs)) return 'version'
  if (bs && budgetSlotHasFileOrPages) return 'budget_slot'
  if (!rubroHasSkillForBudgetBinding(rubro)) return 'no_skill'
  return 'none'
}

export function rubroBudgetSlotHasFileOrPages(
  slots: { budget?: number[] },
  fileIds: { budget?: string }
): boolean {
  const pages = slots.budget
  if (Array.isArray(pages) && pages.length > 0) return true
  const fid = fileIds.budget
  return typeof fid === 'string' && fid.trim().length > 0
}

export function sumBudgetColumnTotal(rows: BudgetSheetGridRow[]): number {
  let s = 0
  for (const row of rows) {
    const v = row.cells?.total
    if (typeof v === 'number' && Number.isFinite(v)) s += v
    else if (typeof v === 'string' && v.trim()) {
      const n = Number(v.replace(',', '.'))
      if (Number.isFinite(n)) s += n
    }
  }
  return s
}
